import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { LazyVideoSourceDirective } from '../../../directives/lazy-video-source.directive';
import { LightboxComponent } from '../lightbox/lightbox.component';
import {
  Gallery,
  GalleryItemView,
  GalleryStoryRailComposition,
} from '../../../../features/projects/interfaces/project.interface';
import { buildGalleryViewModel, resolveShowLightboxThumbnails } from '../../../utils/gallery.utils';
import { GalleryLightboxState } from '../gallery-lightbox.state';

@Component({
  selector: 'app-gallery-filmstrip',
  standalone: true,
  imports: [CommonModule, LightboxComponent, LazyVideoSourceDirective],
  templateUrl: './gallery-filmstrip.component.html',
  styleUrl: './gallery-filmstrip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryFilmstripComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() gallery!: Gallery;
  @Input() assetBase = '';
  @Input() videoBase = '';
  @Input() showGalleryHeading = true;

  @ViewChildren('slideRef') slides!: QueryList<ElementRef<HTMLElement>>;

  readonly lightbox = new GalleryLightboxState();

  public items: GalleryItemView[] = [];
  public pairedItems: { primary: GalleryItemView; companion: GalleryItemView | null; originalIndex: number }[] = [];

  private revealObserver?: IntersectionObserver;
  private fullMotionSlides: HTMLElement[] = [];
  private fullMotionFrameId: number | null = null;
  private fullMotionListenersBound = false;

  private readonly queueFullMotionUpdate = (): void => {
    if (this.fullMotionFrameId !== null || typeof window === 'undefined') {
      return;
    }

    this.fullMotionFrameId = window.requestAnimationFrame(() => {
      this.fullMotionFrameId = null;
      this.updateFullMotionProgress();
    });
  };

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  // ── Getters ─────────────────────────────────────────────────────────────

  public get showLightboxThumbnails(): boolean {
    return resolveShowLightboxThumbnails(this.gallery, this.items.length);
  }

  public get headerLabel(): string {
    const label = this.gallery?.ctaLabel?.trim() || '';
    return label === 'Open premium gallery' ? '' : label;
  }

  public get displayOrderLabel(): string {
    return String((this.gallery?.displayOrder ?? 0) + 1).padStart(2, '0');
  }

  public get currentItem(): GalleryItemView | null {
    return this.items[0] ?? null;
  }

  public get hasMultiple(): boolean {
    return this.items.length > 1;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gallery'] || changes['assetBase'] || changes['videoBase']) {
      const viewModel = buildGalleryViewModel(this.gallery, {
        assetBase: this.assetBase,
        videoBase: this.videoBase,
      });
      this.items = viewModel.items;
      this.pairedItems = this.buildPairedItems(this.items);
      this.lightbox.close();
    }
  }

  ngAfterViewInit(): void {
    this.slides.changes.subscribe(() => {
      this.setupReveal();
    });
    if (this.slides.length > 0) {
      this.setupReveal();
    }
  }

  public ngOnDestroy(): void {
    this.revealObserver?.disconnect();
    this.teardownFullMotion();
    this.lightbox.close();
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  private buildPairedItems(items: GalleryItemView[]) {
    const pairs = [];
    for (let i = 0; i < items.length; i++) {
      if (!this.shouldRenderItem(i, items)) continue;
      const companion = this.getCompanionItem(i, items);
      pairs.push({ primary: items[i], companion, originalIndex: i });
    }
    return pairs;
  }

  public getSlideStagger(index: number): string {
    return `${Math.min(index * 45, 240)}ms`;
  }

  public shouldRenderItem(index: number, items: GalleryItemView[] = this.items): boolean {
    if (index <= 0) {
      return true;
    }

    const previous = items[index - 1];
    const current = items[index];
    if (!previous || !current || current._isText || previous._isText) {
      return true;
    }

    return this.getCompanionItem(index - 1, items) !== current;
  }

  public getSlideClasses(index: number, item: GalleryItemView): string[] {
    const composition = this.getStoryRailComposition(item, index);
    const classes = [
      `filmstrip__slide--composition-${composition}`,
    ];

    if (composition === 'full-parallax' && String(item.body || '').trim()) {
      classes.push('filmstrip__slide--has-parallax-copy');
    }

    const previous = this.items[index - 1];
    if (
      previous
      && this.getStoryRailComposition(previous, index - 1) === 'full-parallax'
      && String(previous.body || '').trim()
    ) {
      classes.push('filmstrip__slide--after-parallax-copy');
    }

    if (
      previous
      && this.getStoryRailComposition(previous, index - 1) === 'full-parallax'
      && !String(previous.body || '').trim()
    ) {
      classes.push('filmstrip__slide--after-parallax');
    }

    return classes;
  }

  public isFullViewportComposition(item: GalleryItemView, index: number): boolean {
    const composition = this.getStoryRailComposition(item, index);
    return composition === 'full-reveal' || composition === 'full-parallax';
  }

  public isParallaxComposition(item: GalleryItemView, index: number): boolean {
    return this.getStoryRailComposition(item, index) === 'full-parallax';
  }

  public getCompanionItem(index: number, items: GalleryItemView[] = this.items): GalleryItemView | null {
    const current = items[index];
    const composition = this.getStoryRailComposition(current, index);

    if (composition !== 'feature-offset' && composition !== 'side-by-side') {
      return null;
    }

    const candidate = items[index + 1];
    if (!current || current._isText || !candidate || candidate._isText) {
      return null;
    }

    // A supporting image may be consumed by the current composition only when
    // it has no scene direction of its own. Full reveals, text breaks, and
    // explicitly composed items must remain standalone slides.
    if (this.getStoryRailComposition(candidate, index + 1) !== 'auto') {
      return null;
    }

    return candidate;
  }

  public getStoryRailComposition(
    item: GalleryItemView | null | undefined,
    index: number
  ): GalleryStoryRailComposition {
    if (!item) {
      return 'auto';
    }

    if (item._isText) {
      return 'text-break';
    }

    if (item._displaySize === 'full') {
      return 'full-parallax';
    }

    if (
      item.storyRailComposition
      && item.storyRailComposition !== 'auto'
      && item.storyRailComposition !== 'text-break'
    ) {
      return item.storyRailComposition;
    }

    if (this.gallery?.filmstripSplitMode && index % 2 === 0) {
      return 'feature-offset';
    }

    return 'auto';
  }

  public getCredit(item: GalleryItemView | null | undefined): string {
    return String(item?.credit || '').trim();
  }

  public getBodyParagraphs(value: unknown): string[] {
    return String(value || '')
      .split(/(?:\r?\n\s*){2,}/)
      .map((paragraph) => paragraph.replace(/\s*\r?\n\s*/g, ' ').trim())
      .filter(Boolean);
  }

  // ── Lightbox ─────────────────────────────────────────────────────────────

  public openLightboxAt(index: number): void {
    this.lightbox.openAt(index);
    this.cdr.markForCheck();
  }

  public onLightboxClose(): void {
    this.lightbox.close();
    this.cdr.markForCheck();
  }

  public onLightboxIndexChange(index: number): void {
    this.lightbox.setIndex(index);
    this.cdr.markForCheck();
  }

  private setupReveal(): void {
    this.revealObserver?.disconnect();
    const slideElements = this.slides.map(slide => slide.nativeElement);
    if (!slideElements.length) return;

    this.setupFullMotion(slideElements);

    if (typeof IntersectionObserver === 'undefined') {
      slideElements.forEach((slide) => slide.classList.add('filmstrip__slide--in'));
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              (entry.target as HTMLElement).classList.add('filmstrip__slide--in');
              this.revealObserver?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.28, rootMargin: '0px 0px -18% 0px' }
      );
      slideElements.forEach((slide) => this.revealObserver!.observe(slide));
    });
  }

  private setupFullMotion(slideElements: HTMLElement[]): void {
    this.fullMotionSlides = slideElements.filter((slide) =>
      slide.classList.contains('filmstrip__slide--composition-full-reveal')
    );
    if (typeof window === 'undefined' || !this.fullMotionSlides.length) {
      this.teardownFullMotion();
      return;
    }

    if (!this.fullMotionListenersBound) {
      this.ngZone.runOutsideAngular(() => {
        window.addEventListener('scroll', this.queueFullMotionUpdate, { passive: true });
        window.addEventListener('resize', this.queueFullMotionUpdate, { passive: true });
      });
      this.fullMotionListenersBound = true;
    }

    this.queueFullMotionUpdate();
  }

  private updateFullMotionProgress(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const viewportHeight = Math.max(window.innerHeight, 1);
    const mobile = window.innerWidth < 768;
    const maximumInset = mobile ? 4.5 : 9;

    this.fullMotionSlides.forEach((slide) => {
      const frame = slide.querySelector<HTMLElement>('.filmstrip__media-frame--full');
      if (!frame) {
        return;
      }

      const rect = frame.getBoundingClientRect();
      const motionTarget = rect;
      const travel = Math.max(viewportHeight + motionTarget.height, 1);
      const scrollProgress = Math.min(1, Math.max(0, (viewportHeight - motionTarget.top) / travel));

      const frameCenter = rect.top + (rect.height / 2);
      const centerDistance = Math.abs(frameCenter - (viewportHeight / 2));
      const centerRange = Math.max((viewportHeight + rect.height) / 2, 1);
      const normalizedDistance = Math.min(1, centerDistance / centerRange);
      // Hold the scene at true viewport width while it is the dominant image.
      // Retraction only begins once the frame is well into its exit phase.
      const fullWidthPlateau = mobile ? 0.28 : 0.34;
      const retractEnd = mobile ? 0.9 : 0.88;
      const retractProgress = Math.min(
        1,
        Math.max(0, (normalizedDistance - fullWidthPlateau) / (retractEnd - fullWidthPlateau))
      );
      const linearOpen = 1 - retractProgress;
      const easedOpen = linearOpen * linearOpen * (3 - (2 * linearOpen));
      const sideInset = (1 - easedOpen) * maximumInset;
      const cornerRadius = (1 - easedOpen) * (mobile ? 8 : 14);

      frame.style.setProperty('--filmstrip-full-clip', `${sideInset.toFixed(3)}%`);
      frame.style.setProperty('--filmstrip-full-radius', `${cornerRadius.toFixed(2)}px`);
    });
  }

  private teardownFullMotion(): void {
    if (typeof window !== 'undefined' && this.fullMotionListenersBound) {
      window.removeEventListener('scroll', this.queueFullMotionUpdate);
      window.removeEventListener('resize', this.queueFullMotionUpdate);
    }

    if (typeof window !== 'undefined' && this.fullMotionFrameId !== null) {
      window.cancelAnimationFrame(this.fullMotionFrameId);
    }

    this.fullMotionSlides = [];
    this.fullMotionFrameId = null;
    this.fullMotionListenersBound = false;
  }
}
